import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import errText from '../../lib/errText';
import { Trophy, CheckCircle, XCircle, Clock, AlertCircle, Users } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const inputStyle = {
  width: '100%', padding: '11px 13px', borderRadius: 8, background: '#0A0A0A',
  border: '1px solid #2A2A2A', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 8,
};

const STATUS_UI = {
  pending:   { color: '#FFB300', bg: '#FFB30014', label: 'Pending Response', icon: Clock },
  accepted:  { color: '#22C55E', bg: '#22C55E14', label: 'Accepted', icon: CheckCircle },
  declined:  { color: '#EF4444', bg: '#EF444414', label: 'Declined', icon: XCircle },
  expired:   { color: '#737373', bg: '#1A1A1A',   label: 'Expired', icon: AlertCircle },
  cancelled: { color: '#737373', bg: '#1A1A1A',   label: 'Cancelled', icon: XCircle },
};

export default function SportsRsvpPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [players, setPlayers] = useState(['']);
  const [divId, setDivId] = useState('');
  const [logoKey, setLogoKey] = useState(null);
  const [logoThumbKey, setLogoThumbKey] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/sports/public/rsvp/${token}`)
      .then(r => {
        setData(r.data);
        const tn = r.data.tournament || {};
        const inv = r.data.invitation || {};
        const size = tn.game_mode === 'singles' ? 1 : (tn.team_size || 2);
        setTeamName(inv.team_name || '');
        setPlayers(Array(size).fill(''));
        setDivId(inv.division_id || '');
      })
      .catch(e => setError(errText(e, 'Invitation not found')))
      .finally(() => setLoading(false));
  }, [token]);

  const slug = data?.facility?.slug;
  const tid = data?.tournament?.id;

  const uploadLogo = async (file) => {
    if (!file || !slug || !tid) return;
    setLogoUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/sports/public/${slug}/tournaments/${tid}/upload-logo`, fd);
      setLogoKey(r.data.logo_key); setLogoThumbKey(r.data.logo_thumb_key);
      setLogoPreview(URL.createObjectURL(file));
    } catch { setError('Logo upload failed — try a PNG or JPG'); }
    finally { setLogoUploading(false); }
  };

  const acceptWithDetails = async () => {
    const cleanPlayers = players.map(n => n.trim()).filter(Boolean).map(n => ({ name: n }));
    if (cleanPlayers.length === 0) { setError('Enter at least one player name'); return; }
    if (data.tournament.divisions?.length > 0 && !divId) { setError('Please select a division'); return; }
    setResponding(true); setError('');
    try {
      const r = await axios.post(`${BACKEND}/api/sports/public/rsvp/${token}/respond`, {
        response: 'accept', team_name: teamName.trim(), players: cleanPlayers,
        division_id: divId || null, logo_key: logoKey, logo_thumb_key: logoThumbKey,
      });
      setDone(r.data.status);
      setData(prev => prev ? { ...prev, invitation: { ...prev.invitation, status: r.data.status } } : prev);
    } catch (e) {
      setError(errText(e, 'Failed to respond'));
    } finally {
      setResponding(false);
    }
  };

  const respond = async (response) => {
    setResponding(true);
    try {
      const r = await axios.post(`${BACKEND}/api/sports/public/rsvp/${token}/respond`, { response });
      setDone(r.data.status);
      setData(prev => prev ? { ...prev, invitation: { ...prev.invitation, status: r.data.status } } : prev);
    } catch (e) {
      setError(errText(e, 'Failed to respond'));
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #FFB300', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', fontFamily: 'Manrope, sans-serif', padding: 20 }}>
        <AlertCircle style={{ width: 48, height: 48, color: '#EF4444', marginBottom: 16 }} />
        <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, marginBottom: 8 }}>Invitation Not Found</h1>
        <p style={{ color: '#737373', fontSize: 14, textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  const inv = data.invitation;
  const tourn = data.tournament;
  const facility = data.facility;
  const st = STATUS_UI[inv.status] || STATUS_UI.pending;
  const StIcon = st.icon;
  const isPending = inv.status === 'pending';

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <header style={{ background: '#0D0D0D', borderBottom: '1px solid #1A1A1A', padding: '16px 20px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#FFB300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trophy style={{ width: 20, height: 20, color: '#0A0A0A' }} />
          </div>
          <div>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#FFB300' }}>
              Tournament Invitation
            </p>
            <p style={{ fontSize: 12, color: '#555' }}>{facility.name}</p>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '32px 20px 60px' }}>
        {/* Status badge */}
        <div data-testid="rsvp-status-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: st.bg, color: st.color, borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 700, marginBottom: 24 }}>
          <StIcon style={{ width: 14, height: 14 }} /> {st.label}
        </div>

        {/* Team info */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#555', marginBottom: 4 }}>Invited Team</p>
          <h1 data-testid="rsvp-team-name" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 32, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, color: '#fff' }}>
            {inv.team_name}
          </h1>
          <p style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{inv.email}</p>
        </div>

        {/* Tournament details */}
        <div data-testid="rsvp-tournament-details" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 14, padding: '20px 22px', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Trophy style={{ width: 18, height: 18, color: '#FFB300' }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 700, color: '#fff' }}>{tourn.name}</h2>
          </div>
          {tourn.description && <p style={{ fontSize: 13, color: '#888', marginBottom: 14, lineHeight: 1.6 }}>{tourn.description}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['Format', tourn.format?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())],
              ['Mode', tourn.game_mode?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())],
              ['Date', tourn.date_start || 'TBA'],
              ['Best Of', `${tourn.best_of} game${tourn.best_of > 1 ? 's' : ''}`],
              ...(tourn.entry_fee > 0 ? [['Entry Fee', `₱${tourn.entry_fee.toLocaleString()}`]] : []),
              ...(tourn.scoring_target ? [['Target', `${tourn.scoring_target} pts`]] : []),
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#0A0A0A', borderRadius: 8, padding: '8px 12px' }}>
                <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#555' }}>{k}</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#ccc', marginTop: 2 }}>{v}</p>
              </div>
            ))}
          </div>
          {!tourn.rsvp_auto_confirm && tourn.entry_fee > 0 && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#1A1200', border: '1px solid #3A2A00', borderRadius: 8 }}>
              <p style={{ fontSize: 12, color: '#FFB300' }}>Entry fee of ₱{tourn.entry_fee.toLocaleString()} must be paid before the tournament starts.</p>
            </div>
          )}
          {(tourn.contact_email || tourn.contact_phone) && (
            <p data-testid="rsvp-contact" style={{ fontSize: 12, color: '#777', marginTop: 14 }}>
              Questions? Contact {tourn.contact_name || 'the organizer'}
              {tourn.contact_email && <> · <a href={`mailto:${tourn.contact_email}`} style={{ color: '#FFB300' }}>{tourn.contact_email}</a></>}
              {tourn.contact_phone && <> · {tourn.contact_phone}</>}
            </p>
          )}
        </div>

        {/* Expiry note */}
        {isPending && inv.expires_at && (
          <p style={{ fontSize: 12, color: '#555', marginBottom: 20 }}>
            <Clock style={{ width: 12, height: 12, display: 'inline', marginRight: 4 }} />
            Respond before {new Date(inv.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        )}

        {/* Error */}
        {error && <p style={{ color: '#EF4444', fontSize: 13, marginBottom: 16 }}>{error}</p>}

        {/* Action buttons */}
        {isPending && !done && !showForm && (
          <div data-testid="rsvp-actions" className="flex gap-3">
            <button data-testid="rsvp-accept-btn" onClick={() => { setError(''); setShowForm(true); }} disabled={responding}
              style={{ flex: 2, padding: '14px', borderRadius: 10, border: 'none', background: '#22C55E', color: '#0A0A0A', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, cursor: responding ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', opacity: responding ? 0.6 : 1 }}>
              Accept Invitation
            </button>
            <button data-testid="rsvp-decline-btn" onClick={() => respond('decline')} disabled={responding}
              style={{ flex: 1, padding: '14px', borderRadius: 10, border: '1px solid #2A2A2A', background: '#141414', color: '#EF4444', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, cursor: responding ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif' }}>
              Decline
            </button>
          </div>
        )}

        {/* Roster + logo form (shown after clicking Accept) */}
        {isPending && !done && showForm && (
          <div data-testid="rsvp-accept-form" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 14, padding: '20px 22px' }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 800, color: '#22C55E', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Confirm Your Squad</p>

            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Team Name</label>
            <input data-testid="rsvp-team-name-input" value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Team name" style={{ ...inputStyle, marginTop: 4 }} />

            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Players</label>
            <div style={{ marginTop: 4 }}>
              {players.map((p, i) => (
                <input key={i} data-testid={`rsvp-player-${i + 1}`} value={p}
                  onChange={e => setPlayers(arr => arr.map((x, idx) => idx === i ? e.target.value : x))}
                  placeholder={`Player ${i + 1}${i === 0 ? ' *' : ''}`} style={inputStyle} />
              ))}
            </div>

            {tourn.divisions?.length > 0 && (
              <>
                <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Division</label>
                <select data-testid="rsvp-division" value={divId} onChange={e => setDivId(e.target.value)} style={{ ...inputStyle, marginTop: 4, cursor: 'pointer' }}>
                  <option value="">Select division *</option>
                  {tourn.divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </>
            )}

            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Team Logo (optional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <div style={{ width: 52, height: 52, borderRadius: 10, border: '1px solid #2A2A2A', overflow: 'hidden', flexShrink: 0, background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {logoPreview ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 10, color: '#525252' }}>Logo</span>}
              </div>
              <input data-testid="rsvp-logo-upload" type="file" accept="image/*" onChange={e => uploadLogo(e.target.files?.[0])} style={{ color: '#A3A3A3', fontSize: 12 }} />
            </div>
            {logoUploading && <p style={{ fontSize: 12, color: '#FFB300', marginTop: 6 }}>Uploading logo…</p>}
            {logoThumbKey && !logoUploading && <p style={{ fontSize: 12, color: '#22C55E', marginTop: 6 }}>✓ Logo attached</p>}

            {error && <p style={{ color: '#EF4444', fontSize: 13, marginTop: 12 }}>{error}</p>}

            <button data-testid="rsvp-confirm-btn" onClick={acceptWithDetails} disabled={responding || logoUploading}
              style={{ width: '100%', marginTop: 16, padding: '14px', borderRadius: 10, border: 'none', background: '#22C55E', color: '#0A0A0A', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, cursor: (responding || logoUploading) ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', opacity: (responding || logoUploading) ? 0.6 : 1 }}>
              {responding ? 'Confirming…' : 'Confirm & Join'}
            </button>
            <button onClick={() => { setShowForm(false); setError(''); }} disabled={responding}
              style={{ width: '100%', marginTop: 8, padding: '10px', borderRadius: 10, border: '1px solid #2A2A2A', background: 'transparent', color: '#777', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Back
            </button>
          </div>
        )}

        {/* Done states */}
        {(done === 'accepted' || inv.status === 'accepted') && (
          <div data-testid="rsvp-accepted-msg" style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircle style={{ width: 48, height: 48, color: '#22C55E', margin: '0 auto 12px' }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, color: '#22C55E', marginBottom: 8 }}>You&apos;re In!</h2>
            <p style={{ fontSize: 14, color: '#888' }}>Team <strong style={{ color: '#fff' }}>{inv.team_name}</strong> has been registered for {tourn.name}.</p>
            {facility.slug && (
              <a href={`/book/${facility.slug}/tournament/${tourn.id}`}
                style={{ display: 'inline-block', marginTop: 16, padding: '10px 24px', borderRadius: 8, background: '#1A1A1A', color: '#FFB300', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                View Tournament
              </a>
            )}
          </div>
        )}
        {(done === 'declined' || inv.status === 'declined') && !isPending && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <XCircle style={{ width: 48, height: 48, color: '#EF4444', margin: '0 auto 12px' }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, color: '#EF4444', marginBottom: 8 }}>Invitation Declined</h2>
            <p style={{ fontSize: 14, color: '#888' }}>The slot has been released.</p>
          </div>
        )}
        {inv.status === 'expired' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <AlertCircle style={{ width: 48, height: 48, color: '#737373', margin: '0 auto 12px' }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, color: '#737373', marginBottom: 8 }}>Invitation Expired</h2>
            <p style={{ fontSize: 14, color: '#555' }}>This invitation is no longer valid.</p>
          </div>
        )}

        {/* Footer */}
        <p style={{ textAlign: 'center', fontSize: 11, color: '#333', marginTop: 40 }}>
          Powered by <a href="/" style={{ color: '#FFB300', textDecoration: 'none', fontWeight: 700 }}>AgriBooks Sports</a>
        </p>
      </div>
    </div>
  );
}
